(function () {
  'use strict';

  /**
   * @typedef {{id: number, title: string, available: boolean, price: number, compareAtPrice: number}} VariantPayload
   * @typedef {{id: string, url: string, name: string, selectedVariantId: number, variants: VariantPayload[]}} ProductPayload
   */
  const CART_KEY = 'deli-dele-cart';
  const existingSite = window.DELI_DELE_SITE || {};
  const GIFT_CARD_PROMO = existingSite.giftCardPromo || { threshold: 1 };
  const strings = existingSite.strings || {};

  const activeCurrency = window.Shopify?.currency?.active || 'USD';
  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: activeCurrency
  });
  const money = value => moneyFormatter.format(Number.isFinite(Number(value)) ? Number(value) : 0);
  const padTime = value => String(value).padStart(2, '0');
  const getProducts = () => window.DELI_DELE_SITE?.products || [];

  const getProductById = id => {
    const products = getProducts();
    if (!id) return products[0] || null;
    return products.find(product => product.id === id) || null;
  };
  const getProductHandleByVariantId = (variantId) => {
    const product = getProducts().find(candidate =>
      (candidate.variants || []).some(variant => Number(variant.id) === Number(variantId))
    );
    return product?.id || null;
  };

  // Shopify's /cart.js returns quantities as a cart object with a token
  // and `items`. We use the line_item `key` instead of variant id because
  // the key is the stable line identifier for updates/removals.
  const normalizeShopifyItem = (item) => {
    const rawHandle = item.handle || item.url?.split('/').pop()?.split('?')[0];
    const id = rawHandle || getProductHandleByVariantId(item.variant_id);
    if (!id) return null;
    return {
      id,
      qty: item.quantity,
      key: item.key,
      variantId: item.variant_id,
      properties: item.properties || null,
      sellingPlanAllocation: item.selling_plan_allocation || null,
      sellingPlanId: item.selling_plan_allocation?.selling_plan?.id || (item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan_id : null) || null,
      sellingPlanName: item.selling_plan_allocation?.selling_plan?.name || null,
      price: typeof item.price === 'number' ? item.price / 100.0 : null,
      originalPrice: typeof item.original_price === 'number' ? item.original_price / 100.0 : null,
      finalPrice: typeof item.final_price === 'number' ? item.final_price / 100.0 : null,
      linePrice: typeof item.final_line_price === 'number' ? item.final_line_price / 100.0 : (typeof item.line_price === 'number' ? item.line_price / 100.0 : null)
    };
  };

  const refreshFromShopifyCart = async () => {
    try {
      const res = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      const shopifyCart = await res.json();
      const fresh = [];
      for (const item of shopifyCart.items) {
        const normalized = normalizeShopifyItem(item);
        if (normalized) {
          fresh.push(normalized);
        }
      }
      localStorage.setItem(CART_KEY, JSON.stringify(fresh));
      window.dispatchEvent(new CustomEvent('deli-dele:cart-updated', { detail: { cart: fresh } }));
      updateCartCount();
      return fresh;
    } catch (e) {
      console.error('Error refreshing from Shopify cart:', e);
      return getCart();
    }
  };

  // Do not overwrite the local cart on every page load; the Shopify store
  // front will rehydrate the cart via /cart.js on the cart page and after
  // explicit add/update/remove calls.

  const getCart = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
      return Array.isArray(parsed)
        ? parsed.filter(item => item && typeof item.id === 'string' && Number(item.qty) > 0)
        : [];
    } catch (error) {
      console.error('Error reading local cart:', error);
      return [];
    }
  };
  const saveCart = cart => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    window.dispatchEvent(new CustomEvent('deli-dele:cart-updated', { detail: { cart } }));
    return cart;
  };
  const setCart = cart => saveCart(cart);
  const cartCount = () => getCart().reduce((sum, item) => sum + item.qty, 0);
  const qualifiesForGiftCard = cart => cart.reduce((sum, item) => sum + item.qty, 0) >= GIFT_CARD_PROMO.threshold;
  
  const addToCart = async (id, qty = 1, variantId = null, properties = null, sellingPlan = null) => {
    const canonicalId = id;

    // Standard Shopify AJAX add to cart. When a variant id is provided we
    // use that exact product/variant; otherwise fall back to the first variant
    // of the mapped handle.
    const targetProduct = getProductById(canonicalId);
    const targetVariantId = variantId || targetProduct?.selectedVariantId || targetProduct?.variants?.[0]?.id;
    if (targetVariantId) {
      try {
        const payload = { id: Number(targetVariantId), quantity: Number(qty) };
        if (properties) {
          payload.properties = properties;
        }
        if (sellingPlan) {
          payload.selling_plan = Number(sellingPlan);
        }
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || `Shopify add failed (${res.status})`);
        }
        await refreshFromShopifyCart();
        return getCart();
      } catch (e) {
        console.error('Error adding to Shopify cart:', e);
        throw e;
      }
    }

    // Fallback / optimistic local update.
    const cart = getCart();
    const existing = cart.find(item => 
      item.id === canonicalId && 
      Number(item.variantId) === Number(targetVariantId) &&
      String(item.sellingPlanId || '') === String(sellingPlan || '')
    );
    if (existing) existing.qty += qty;
    else cart.push({
      id: canonicalId,
      qty,
      variantId: Number(targetVariantId) || null,
      sellingPlanId: sellingPlan ? Number(sellingPlan) : null,
      properties: properties || null
    });
    saveCart(cart);
    return cart;
  };

  const updateCartItemQty = async (reference, delta) => {
    const cart = getCart();
    const line = cart.find(item => item.key === reference || item.id === reference);
    if (!line) return saveCart(cart);
    const newLocalQty = Math.max(0, line.qty + delta);

    try {
      const cartRes = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      const shopifyCart = await cartRes.json();
      const item = shopifyCart.items.find(x => x.key === line.key || Number(x.variant_id) === Number(line.variantId));
      if (item) {
        const shopifyQty = Math.max(0, item.quantity + delta);
        const changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: String(item.key), quantity: shopifyQty })
        });
        if (!changeRes.ok) throw new Error(`Shopify change failed (${changeRes.status})`);
        await refreshFromShopifyCart();
        return getCart();
      }
    } catch (e) {
      console.error('Error updating Shopify cart quantity:', e);
    }

    // Optimistic local fallback.
    line.qty = newLocalQty;
    const filtered = cart.filter(item => item.qty > 0);
    return saveCart(filtered);
  };

  const removeFromCart = async reference => {
    const cart = getCart();
    const removed = cart.find(item => item.key === reference || item.id === reference) || null;
    if (!removed) return null;

    try {
      const cartRes = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      const shopifyCart = await cartRes.json();
      const itemsToRemove = shopifyCart.items.filter(x => x.key === removed.key || Number(x.variant_id) === Number(removed.variantId));
      const promises = itemsToRemove.map(item => {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: String(item.key), quantity: 0 })
        });
      });
      await Promise.all(promises);
      await refreshFromShopifyCart();
      return removed;
    } catch (e) {
      console.error('Error removing from Shopify cart:', e);
    }

    // Optimistic local fallback.
    const filtered = cart.filter(item => item !== removed);
    saveCart(filtered);
    return removed;
  };

  const restoreCartItem = removedItem => {
    if (!removedItem) return getCart();
    return addToCart(removedItem.id, removedItem.qty, removedItem.variantId, removedItem.properties, removedItem.sellingPlanId);
  };
  const seedCart = items => {
    if (getCart().length === 0) saveCart(items);
  };
  const updateCartCount = () => {
    const count = cartCount();
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = count; });
  };

  const getLaunchCountdownParts = (target, now = new Date()) => {
    const remainingMs = Math.max(0, target.getTime() - now.getTime());
    const totalSeconds = Math.floor(remainingMs / 1000);

    return {
      complete: remainingMs === 0,
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };
  };
  let launchAnnouncementIntervalId = null;

  const initLaunchAnnouncement = () => {
        if (launchAnnouncementIntervalId) {
      window.clearInterval(launchAnnouncementIntervalId);
      launchAnnouncementIntervalId = null;
    }

    const bars = Array.from(document.querySelectorAll('[data-launch-announcement]'));

    if (bars.length === 0) return;

    const barConfigs = bars.map(bar => {
      if (bar.getAttribute('data-disabled') === 'true') {
        return null;
      }
      const dataTargetDate = bar.getAttribute('data-target-date');
      const dataLabel = bar.getAttribute('data-label');
      const dataLiveMessage = bar.getAttribute('data-live-message');
      const countdownEnabled = bar.getAttribute('data-countdown-enabled') === 'true';

      // let target = new Date(0);
      let target =  null;
      if (dataTargetDate) {
        const parts = dataTargetDate.split('-');
        if (parts.length === 3) {
          target = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 0, 0, 0);
        }
      }

      return {
        element: bar,
        label: dataLabel || '',
        liveMessage: dataLiveMessage || '',
        target: target,
        countdownEnabled: countdownEnabled
      };
    }).filter(Boolean);

    if (barConfigs.length === 0) return;

    // let intervalId;

    const render = () => {
      let allComplete = true;

      barConfigs.forEach(config => {
        const bar = config.element;
       const parts = config.target ? getLaunchCountdownParts(config.target) : { complete: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
        if (!parts.complete && config.countdownEnabled) {
          allComplete = false;
        }

        const messages = bar.querySelectorAll('[data-launch-message]');
        const countdowns = bar.querySelectorAll('[data-launch-countdown]');
        const daysList = bar.querySelectorAll('[data-launch-days]');
        const hoursList = bar.querySelectorAll('[data-launch-hours]');
        const minutesList = bar.querySelectorAll('[data-launch-minutes]');
        const secondsList = bar.querySelectorAll('[data-launch-seconds]');

        messages.forEach(el => {
          // el.textContent = parts.complete ? config.liveMessage : config.label;
          const beforeMsg = el.getAttribute('data-message-before');
          const afterMsg = el.getAttribute('data-message-after');

          if (config.countdownEnabled && parts.complete && afterMsg && afterMsg.trim() !== '') {
            el.textContent = afterMsg;
          } else if (beforeMsg && beforeMsg.trim() !== '') {
            el.textContent = beforeMsg;
          } else if (parts.complete && config.liveMessage) {
            el.textContent = config.liveMessage;
          } else if (config.label) {
            el.textContent = config.label;
          }
        });

        countdowns.forEach(countdown => {
          countdown.dataset.state = parts.complete ? 'live' : 'counting';
          countdown.setAttribute(
            'aria-label',
            parts.complete
              ? (config.liveMessage || '')
              : Object.entries({
                days: parts.days,
                hours: parts.hours,
                minutes: parts.minutes,
                seconds: parts.seconds
              }).reduce((message, [key, value]) => message.replaceAll(`[${key}]`, String(value)), existingSite.strings?.launchCountdown || '')
          );
        });

        daysList.forEach(el => { el.textContent = String(parts.days); });
        hoursList.forEach(el => { el.textContent = padTime(parts.hours); });
        minutesList.forEach(el => { el.textContent = padTime(parts.minutes); });
        secondsList.forEach(el => { el.textContent = padTime(parts.seconds); });
      });

      if (allComplete && launchAnnouncementIntervalId) {
        window.clearInterval(launchAnnouncementIntervalId);
        launchAnnouncementIntervalId = null;
      }
    };

    render();

    const anyNotComplete = barConfigs.some(config => config.countdownEnabled && config.target && !getLaunchCountdownParts(config.target).complete);
     if (anyNotComplete) {
    launchAnnouncementIntervalId = window.setInterval(render, 1000);
 
    }
  };

  initLaunchAnnouncement();

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && (event.target.querySelector('[data-launch-announcement]') || event.target.matches('[data-launch-announcement]'))) {
      initLaunchAnnouncement();
    }
  });

  window.DELI_DELE_SITE = {
    ...existingSite,
    CART_KEY,
    products: existingSite.products || [],
    stories: existingSite.stories || [],
    giftCardPromo: existingSite.giftCardPromo || GIFT_CARD_PROMO,
    purchaseOptions: existingSite.purchaseOptions || [],
    subscriptionDetail: existingSite.subscriptionDetail || null,
    money,
    getProducts,
    getProductById,
    getCart,
    setCart,
    cartCount,
    qualifiesForGiftCard,
    addToCart,
    updateCartItemQty,
    removeFromCart,
    restoreCartItem,
    seedCart,
    updateCartCount,
    refreshFromShopifyCart
  };
})();
